import React from 'react';
import { logError } from '../utils/errorHandler';

/**
 * React Error Boundary component
 * Catches JavaScript errors anywhere in the component tree and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error with context
    logError('ErrorBoundary', error, {
      componentStack: errorInfo.componentStack
    });

    this.setState({
      error,
      errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)'
        }}>
          <h1 style={{
            fontSize: '2rem',
            marginBottom: '1rem',
            color: 'var(--error-color)'
          }}>
            Oops! Something went wrong
          </h1>
          <p style={{
            fontSize: '1rem',
            marginBottom: '2rem',
            color: 'var(--text-secondary)'
          }}>
            We're sorry for the inconvenience. Please try refreshing the page.
          </p>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{
              maxWidth: '800px',
              width: '100%',
              textAlign: 'left',
              backgroundColor: 'var(--bg-secondary)',
              padding: '1rem',
              borderRadius: 'var(--radius-md)',
              marginTop: '1rem'
            }}>
              <summary style={{
                cursor: 'pointer',
                fontWeight: 'bold',
                marginBottom: '1rem',
                color: 'var(--text-primary)'
              }}>
                Error Details (Development Mode)
              </summary>
              <pre style={{
                fontSize: '0.875rem',
                overflow: 'auto',
                color: 'var(--error-color)'
              }}>
                {this.state.error.toString()}
                {'\n\n'}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}

          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '2rem',
              padding: 'var(--space-sm) var(--space-lg)',
              fontSize: 'var(--font-size-md)',
              backgroundColor: 'var(--button-primary-bg)',
              color: 'var(--button-primary-text)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
